import { createRef, type default as React, useCallback, useMemo, useState } from 'react';

import { arrowKeys } from './constants';
import { type Direction, type Indexes, type Item } from './types';
import { findItemIndexInTable, getClosestInTable, getItem, move } from './utils';

/**
 * Hook to manage focus state in a table.
 * @param table - Table of items to focus on.
 * @param epoch - Epoch to focus near initially.
 * @returns Focused tab, tabbed, focusRefs, onContainerKeyDown,.
 * @var tab - Function to make specific item tabbable.
 * @var tabbed - Currently tabbale item.
 * @var focusRefs - Refs to focusable items.
 * @var onContainerKeyDown - Function to handle keydown on container.
 */
const useShiftFocus = (table: Item[][], epoch: number) => {
	const [tabable, setTabable] = useState<Indexes | null>(() => getClosestInTable(table, epoch));

	const focusRefs = useMemo(
		() =>
			table
				.flat()
				.reduce(
					(acc, { id }) => ({ ...acc, [id]: createRef<HTMLDivElement>() }),
					{} as Record<string, React.RefObject<HTMLDivElement>>,
				),
		[table],
	);

	const focus = useCallback(
		(indexes: Indexes | null) => {
			const item = getItem(table, indexes);

			if (item) {
				focusRefs[item.id].current?.focus();
			}
		},
		[focusRefs, table],
	);

	const handleMove = useMemo(() => move(table), [table]);

	const navigate = useCallback(
		(direction: Direction) => {
			if (tabable === null) {
				return null;
			}

			setTabable((prev) => {
				if (prev) {
					const next = handleMove(prev, direction);

					focus(next);

					return next;
				}

				return null;
			});
		},
		[tabable, handleMove, focus],
	);

	const onContainerKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			const { key } = e;

			const direction = key in arrowKeys ? arrowKeys[key] : null;

			if (direction) {
				e.preventDefault();
				navigate(direction);
			}
		},
		[navigate],
	);

	const tab = useCallback(
		(id: string) => {
			setTabable(findItemIndexInTable(table, id));
		},
		[table],
	);

	const tabbed = getItem(table, tabable);

	return {
		tab,
		tabbed,
		focusRefs,
		onContainerKeyDown,
	};
};

export { useShiftFocus };
